import { useMemo } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { CaretIcon } from '@icons/CaretIcon';
import { CaretIconUp } from '@icons/CaretIconUp';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import { validateArtworkUrl } from '@insights_module/utils/showsUtils';
import { Spacer } from '@components/Spacer';
import { removeShowTitleFromEpisodeTitle } from '@insights_module/utils/episodesUtils';
import { ArrowToRight } from '@icons/ArrowToRight';
import { useEpisodeDetailsData } from '@insights_module/hooks/useEpisodeDetailsData';

const Header = tw.div`flex flex-row items-center justify-between w-full max-h-[40px] p-3 border-b border-gray-1`;
const FlexContainer = tw.div`flex items-center justify-between`;
const StyledButton = tw.button`h-[26px] w-[25px] rounded border border-solid border-gray-1 inline-flex items-center justify-center overflow-hidden hover:bg-gray-2`;
const BackButton = tw.div`min-w-[48px] h-[18px] flex items-center justify-center text-[8px] bg-gray-3 rounded cursor-pointer uppercase gap-1 text-text-and-icon`;
const EpisodesCount = tw.p`block text-text-and-icon-muted text-[13px]`;

const BreadCrumbsContainer = tw.div`w-full flex items-center justify-start ml-3`;
const BreadCrumbsThumbnail = tw.img`h-6 w-6 rounded mr-2`;
const BreadCrumbsItem = tw.span`text-[13px] text-text-and-icon font-semibold truncate w-auto max-w-[80%]`;
const BreadCrumbsClickable = tw(
  BreadCrumbsItem
)`text-text-and-icon-muted font-normal cursor-pointer mr-1`;

const disabledButtonStyles = tw`text-text-and-icon-muted cursor-not-allowed opacity-60`;

const BreadCrumbs = ({
  thumbnail,
  showTitle,
  episodeTitle,
}: {
  thumbnail: string | undefined;
  showTitle: string | undefined;
  episodeTitle: string | undefined;
  onClick: () => void;
}): JSX.Element | null => {
  if (!showTitle || !episodeTitle) return null;

  const truncatedTitle = removeShowTitleFromEpisodeTitle(episodeTitle, showTitle);

  return (
    <BreadCrumbsContainer>
      <BreadCrumbsThumbnail src={validateArtworkUrl(thumbnail)} alt={showTitle} />
      <BreadCrumbsClickable
        title={showTitle}
        // onClick is currently not used because in case of episodes table selected as main, click on show name resulting going back to episodes is not intuitive
        //  onClick={onClick}
      >
        {showTitle}
      </BreadCrumbsClickable>
      {!!truncatedTitle && (
        <BreadCrumbsItem title={truncatedTitle}>/ {truncatedTitle}</BreadCrumbsItem>
      )}
    </BreadCrumbsContainer>
  );
};

export const EpisodeDetailsHeader = () => {
  const { t } = useTranslation();
  const { setSelectedEpisode, selectedEpisode } = useInsightsContext();
  const {
    getPrevEpisode,
    getNextEpisode,
    getEpisodeIndex,
    episodesTotalCount,
    selectedEpisodeDetails: currentEpisode,
  } = useEpisodeDetailsData();

  const {
    setSelectedSegment,
    setSelectedContextualCategory,
    setSelectedGarmCategory,
    setSelectedGarmLevel,
    setSelectedEntity,
  } = useEpisodeDetailsContext();

  const currentEpisodeIndex = useMemo(
    () => getEpisodeIndex(selectedEpisode),
    [selectedEpisode, currentEpisode]
  );

  const resetSelected = () => {
    setSelectedSegment(null);
    setSelectedContextualCategory(null);
    setSelectedGarmCategory(null);
    setSelectedGarmLevel(null);
    setSelectedEntity(null);
  };

  const handleGoBack = () => {
    setSelectedEpisode('');
    resetSelected();
  };

  const handleNext = () => {
    const episode = getNextEpisode(selectedEpisode);
    resetSelected();

    if (episode?.id) {
      setSelectedEpisode(episode.id);
    }
  };

  const handlePrev = () => {
    const episode = getPrevEpisode(selectedEpisode);
    resetSelected();

    if (episode?.id) {
      setSelectedEpisode(episode.id);
    }
  };

  return (
    <Header>
      <div tw='flex items-center justify-start max-w-[80%]'>
        <BackButton onClick={handleGoBack}>
          <ArrowToRight className='rotate-180' tw='stroke-text-and-icon-muted w-1.5 h-1.5' />
          {t('common.back')}
        </BackButton>
        <BreadCrumbs
          thumbnail={currentEpisode?.coverImage}
          episodeTitle={currentEpisode?.title}
          showTitle={currentEpisode?.showTile}
          onClick={handleGoBack}
        />
      </div>
      <FlexContainer>
        <EpisodesCount>{`${currentEpisodeIndex + 1} / ${episodesTotalCount.toLocaleString()}`}</EpisodesCount>
        <Spacer direction='horizontal' size='xs' />
        <StyledButton onClick={handlePrev} css={[!currentEpisodeIndex && disabledButtonStyles]}>
          <CaretIconUp tw='stroke-text-and-icon w-2' />
        </StyledButton>
        <Spacer direction='horizontal' size='xxs' />
        <StyledButton
          onClick={handleNext}
          tw='mr-1'
          css={[currentEpisodeIndex === episodesTotalCount - 1 && disabledButtonStyles]}>
          <CaretIcon tw='fill-text-and-icon w-2' />
        </StyledButton>
      </FlexContainer>
    </Header>
  );
};
